import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  addDays,
  addMonths,
  endOfWeek,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { CircleIconButton } from '@/components/ui/CircleIconButton';
import { toLocalIsoDate } from '@/utils/localDate';
import styles from './styles.module.scss';

/** Always six rows so the container height stays stable between months. */
const WEEKS_IN_GRID = 6;
const DAYS_IN_WEEK = 7;

export interface MonthCalendarProps {
  /** Selected day (local date). */
  value: Date;
  today: Date;
  onSelect: (date: Date) => void;
  /** BCP 47 language used for month / weekday names (typically `i18n.language`). */
  locale: string;
  /** First day of the week; defaults to Monday for `ru`, Sunday otherwise. */
  weekStartsOn?: 0 | 1;
  /** Move DOM focus to the selected day on mount (dialog-style date pickers). */
  autoFocus?: boolean;
  /** Enables `${prefix}-day-YYYY-MM-DD`, `${prefix}-prev-month`, `${prefix}-next-month` test ids. */
  testIdPrefix?: string;
}

function capitalizeFirst(text: string, locale: string): string {
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

/**
 * Month grid date picker (7 × 6 tiles) with ARIA grid semantics and roving
 * tabindex. Pure presentation: popover / sheet handling belongs to the caller.
 *
 * The visible month is derived from the keyboard-focused day, so arrowing past
 * the edge of the month switches months. Outside-month tiles are regular days:
 * activating one selects it via `onSelect`, like any other tile.
 */
export const MonthCalendar: React.FC<MonthCalendarProps> = ({
  value,
  today,
  onSelect,
  locale,
  weekStartsOn = locale.startsWith('ru') ? 1 : 0,
  autoFocus = false,
  testIdPrefix,
}) => {
  const { t } = useTranslation(['league']);
  const titleId = useId();
  const gridRef = useRef<HTMLDivElement>(null);
  const [focusedDate, setFocusedDate] = useState<Date>(value);
  // Raised by keyboard navigation (and by `autoFocus` on mount) so the effect
  // below moves DOM focus onto the new active tile. Month buttons leave focus
  // on themselves.
  const moveFocusRef = useRef(autoFocus);

  useEffect(() => {
    if (!moveFocusRef.current) return;
    moveFocusRef.current = false;
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-date="${toLocalIsoDate(focusedDate)}"]`)
      // preventScroll: on mount the tile may sit inside a sheet that is still
      // sliding in; scrolling it into view would jolt the page behind.
      ?.focus({ preventScroll: true });
  }, [focusedDate]);

  const formatters = useMemo(() => ({
    monthTitle: new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
    weekdayShort: new Intl.DateTimeFormat(locale, { weekday: 'short' }),
    weekdayLong: new Intl.DateTimeFormat(locale, { weekday: 'long' }),
    fullDate: new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  }), [locale]);

  const viewMonth = startOfMonth(focusedDate);
  const viewMonthKey = viewMonth.getTime();

  const weeks = useMemo(() => {
    const gridStart = startOfWeek(new Date(viewMonthKey), { weekStartsOn });
    return Array.from({ length: WEEKS_IN_GRID }, (_, week) =>
      Array.from({ length: DAYS_IN_WEEK }, (__, day) =>
        addDays(gridStart, week * DAYS_IN_WEEK + day),
      ),
    );
  }, [viewMonthKey, weekStartsOn]);

  // Column headers come from the first grid row, so they always line up with
  // `weekStartsOn`.
  const firstWeek = weeks[0] ?? [];

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    let next: Date;
    switch (event.key) {
      case 'ArrowLeft': next = addDays(focusedDate, -1); break;
      case 'ArrowRight': next = addDays(focusedDate, 1); break;
      case 'ArrowUp': next = addDays(focusedDate, -DAYS_IN_WEEK); break;
      case 'ArrowDown': next = addDays(focusedDate, DAYS_IN_WEEK); break;
      case 'PageUp': next = addMonths(focusedDate, -1); break;
      case 'PageDown': next = addMonths(focusedDate, 1); break;
      case 'Home': next = startOfWeek(focusedDate, { weekStartsOn }); break;
      case 'End': next = endOfWeek(focusedDate, { weekStartsOn }); break;
      // Enter / Space activate the native <button> click.
      default: return;
    }
    event.preventDefault();
    moveFocusRef.current = true;
    setFocusedDate(next);
  };

  return (
    <div className={styles.calendar}>
      <div className={styles.header}>
        <CircleIconButton
          icon="chevron-left"
          size="sm"
          ariaLabel={t('league:previousMonth')}
          onClick={() => setFocusedDate(addMonths(focusedDate, -1))}
          testId={testIdPrefix ? `${testIdPrefix}-prev-month` : undefined}
        />
        <div id={titleId} className={styles.title} aria-live="polite">
          {capitalizeFirst(formatters.monthTitle.format(viewMonth), locale)}
        </div>
        <CircleIconButton
          icon="chevron-right"
          size="sm"
          ariaLabel={t('league:nextMonth')}
          onClick={() => setFocusedDate(addMonths(focusedDate, 1))}
          testId={testIdPrefix ? `${testIdPrefix}-next-month` : undefined}
        />
      </div>

      <div ref={gridRef} role="grid" aria-labelledby={titleId} className={styles.grid}>
        <div role="row" className={styles.row}>
          {firstWeek.map(day => (
            <div
              key={day.getDay()}
              role="columnheader"
              aria-label={formatters.weekdayLong.format(day)}
              className={styles.weekday}
            >
              {capitalizeFirst(formatters.weekdayShort.format(day), locale)}
            </div>
          ))}
        </div>

        {weeks.map(week => (
          <div key={toLocalIsoDate(week[0] ?? viewMonth)} role="row" className={styles.row}>
            {week.map(day => {
              const iso = toLocalIsoDate(day);
              const isSelected = isSameDay(day, value);
              const classNames = [styles.day];
              if (!isSameMonth(day, viewMonth)) classNames.push(styles.outside);
              if (isSameDay(day, today)) classNames.push(styles.today);
              if (isSelected) classNames.push(styles.selected);

              return (
                <button
                  key={iso}
                  type="button"
                  role="gridcell"
                  className={classNames.join(' ')}
                  tabIndex={isSameDay(day, focusedDate) ? 0 : -1}
                  aria-selected={isSelected}
                  aria-current={isSameDay(day, today) ? 'date' : undefined}
                  aria-label={formatters.fullDate.format(day)}
                  data-date={iso}
                  data-testid={testIdPrefix ? `${testIdPrefix}-day-${iso}` : undefined}
                  onClick={() => onSelect(day)}
                  onKeyDown={handleKeyDown}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthCalendar;
